import {
  EventType,
  IAppointment,
  ICalendarEventSchedulesMap,
  ICandidate,
  ICandidateCalendarEvent,
  IEvent,
  ILabJob,
  IPractice,
  IScheduleSummaryEventable,
  IStaffer,
  IWaitListCandidate,
  ParticipantType,
  ResolvedAppointmentDependency,
  isLabJob,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  Firestore,
  INamedDocument,
  IReffable,
  WithRef,
  asDocRef,
  asReffable,
  asyncForEach,
  deleteDoc,
  getEnumValues,
  multiFilter,
  multiSwitchMap,
  snapshot,
  timePeriodsIntersect,
  toMoment,
  toNamedDocument,
  toTimePeriod,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { compact, difference, first } from 'lodash';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Appointment } from '../appointment/appointment';
import { Brand } from '../brand';
import { CandidateCalendarEvent } from '../event/calendar-event';
import { Event } from '../event/event';
import { EventableQueries } from '../eventable-queries';
import { Patient } from '../patient/patient';
import { Roster } from '../staffer/roster';

export async function waitListCandidateToEvent(
  gap: IScheduleSummaryEventable,
  waitlistCandidate: IWaitListCandidate,
  user: INamedDocument<IStaffer>
): Promise<ICandidateCalendarEvent> {
  const candidate: ICandidate = waitlistCandidate.candidate;
  const waitlistParticipant = await Appointment.patient(
    waitlistCandidate.appointment
  );
  return CandidateCalendarEvent.init({
    candidate: candidate,
    event: Event.init({
      from: candidate.offerTimeFrom,
      to: candidate.offerTimeTo,
      type: EventType.GapCandidate,
      practice: {
        name: waitlistCandidate.appointment.practiceName,
        ref: asDocRef<IPractice>(waitlistCandidate.appointment.practiceRef),
      },
      participants: [
        ...gap.event.participants,
        {
          ...toNamedDocument(waitlistParticipant),
          type: ParticipantType.Patient,
        },
      ],
      creator: toNamedDocument(user),
    }),
  });
}

export async function removeCandidateFromAllShortlists(
  appointment: IReffable<IAppointment>
): Promise<void> {
  const patientRef = Appointment.patientRef(appointment);
  const brandRef = Patient.brandRef(asReffable(patientRef));
  const candidateEvents = await Firestore.getDocs(
    undeletedQuery(Brand.calendarEventCol(asReffable(brandRef))),
    where('candidate.appointment', '==', appointment.ref)
  );

  await asyncForEach(candidateEvents, ({ ref }) => deleteDoc(ref));
}

export async function removeConflictingCandidates(
  event: IEvent
): Promise<void> {
  const candidates = await getConflictingCandidates(event);
  await asyncForEach(candidates, ({ ref }) => Firestore.deleteDoc(ref));
}

export async function getConflictingCandidates(
  event: IEvent
): Promise<WithRef<ICandidateCalendarEvent>[]> {
  const stafferRef = first(Event.staff(event))?.ref;
  if (!stafferRef) {
    return [];
  }

  const practice = await Firestore.getDoc(event.practice.ref);
  const staffer = await Firestore.getDoc(stafferRef);
  const stafferRoster: ICalendarEventSchedulesMap = {
    [stafferRef.id]: await snapshot(
      Roster.all$(staffer, [EventType.RosteredOn], practice)
    ),
  };
  const range = toTimePeriod(
    toMoment(event.from).startOf('day'),
    toMoment(event.from).endOf('day')
  );

  return snapshot(
    EventableQueries.getScheduleSummaryEventablesWithFallback$(
      range,
      practice,
      [staffer],
      stafferRoster,
      difference(getEnumValues(EventType), [EventType.GapCandidate])
    ).pipe(
      multiFilter(({ event: scheduleSummaryEvent }) =>
        timePeriodsIntersect(
          Event.toTimePeriod(scheduleSummaryEvent),
          Event.toTimePeriod(event),
          true,
          'minute'
        )
      ),
      multiSwitchMap((scheduleSummaryEvent) =>
        scheduleSummaryEvent.ref
          ? Firestore.getDoc(
              asDocRef<ICandidateCalendarEvent>(scheduleSummaryEvent.ref)
            )
          : of(undefined)
      ),
      map(compact)
    )
  );
}

export class WaitListCandidate {
  static init(
    overrides: AtLeast<IWaitListCandidate, 'candidate' | 'appointment'>
  ): IWaitListCandidate {
    return {
      productivity: 1,
      ...overrides,
    };
  }

  static getWarnings$(
    appointment: WithRef<IAppointment>,
    event: IEvent
  ): Observable<string[]> {
    return Appointment.dependencies$(appointment).pipe(
      startWith([]),
      map((dependencies) => {
        return this.getDependencyWarnings(dependencies, event);
      })
    );
  }

  static getDependencyWarnings(
    dependencies: ResolvedAppointmentDependency[],
    event: IEvent
  ): string[] {
    const labJobWarnings: string[] = dependencies
      .filter((dependency) => isLabJob(dependency))
      .map((labJob) => this.getLabJobWarning(labJob, event));

    return [...labJobWarnings].filter((warning: string) => warning !== '');
  }

  static getLabJobWarning(labJob: ILabJob, event: IEvent): string {
    const labJobDue: Moment = labJob.dueDate
      ? toMoment(labJob.dueDate)
      : moment();
    const eventStart: Moment = toMoment(event.from);
    if (labJobDue.isAfter(eventStart)) {
      return `Required Lab Job is due before this appointment`;
    }
    return '';
  }
}
